import { useGetOneExpeditionBo, useUpdateReturnBo } from "@/api/expedition.api";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  GetAllExpeditionBoResponseDtoExpedition,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import {
  getReturnModalDefaultValues,
  ReturnModalData,
  ReturnModalSchema,
  toReturnUpdatePayload,
} from "./return.form";
import { useExpeditionBlockedReasonOptions } from "@/hooks/useEnumOptions";

type Props = {
  open: boolean;
  onClose: () => void;
  expedition: GetAllExpeditionBoResponseDtoExpedition | GetOneExpeditionBoResponseDto;
};

export const ReturnModal = ({ open, onClose, expedition }: Props) => {
  const { t } = useTranslation("boExpeditions");
  const {
    data: expeditionReturnBillingDetail,
    isLoading,
    isError,
  } = useGetOneExpeditionBo(expedition.id);

  if (isLoading) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={t("returnModal.title")}
        description={t("returnModal.description")}
      >
        <div className="flex w-full justify-center py-8">
          <Loader2 className="animate-spin" />
        </div>
      </Modal>
    );
  }

  if (isError || !expeditionReturnBillingDetail) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={t("returnModal.title")}
        description={t("returnModal.description")}
      >
        <p className="py-8 text-center text-sm text-red-600">{t("returnModal.loadError")}</p>
      </Modal>
    );
  }

  return <ModalContent expedition={expeditionReturnBillingDetail} open={open} onClose={onClose} />;
};

const ModalContent = ({
  expedition,
  open,
  onClose,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const blockedReasonOptions = useExpeditionBlockedReasonOptions();
  const returnForm = useForm<ReturnModalData>({
    defaultValues: getReturnModalDefaultValues(expedition),
    resolver: zodResolver(ReturnModalSchema),
    mode: "onTouched",
  });

  const { mutateAsync: updateReturnBo, isPending: isUpdatingReturnBo } = useUpdateReturnBo();

  const onSubmit: SubmitHandler<ReturnModalData> = async (data) => {
    await updateReturnBo({
      id: expedition.id,
      payload: toReturnUpdatePayload(data),
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={t("returnModal.title")}
      description={t("returnModal.description")}
      footer={
        <div className="flex w-full flex-row justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={isUpdatingReturnBo}
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            type="submit"
            form="return-modal-form"
            variant="default"
            className="flex-1"
            disabled={isUpdatingReturnBo}
          >
            {isUpdatingReturnBo ? <Loader2 className="animate-spin" /> : t("returnModal.submit")}
          </Button>
        </div>
      }
    >
      <form
        id="return-modal-form"
        onSubmit={returnForm.handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <fieldset disabled={isUpdatingReturnBo} className="contents">
          <ControlledSelectV2
            control={returnForm.control}
            name="blockedReason"
            label={t("returnModal.reasonLabel")}
            options={blockedReasonOptions}
          />
          <p className="text-sm text-gray-600">{t("returnModal.amountToBill")}</p>
          <div className="grid w-full grid-cols-3 items-center justify-between gap-4">
            <ControlledUnifiedInput
              control={returnForm.control}
              name="transportHt"
              label={t("returnModal.transportHtLabel")}
              inputType="float"
              rightElement={<span>€</span>}
            />
            <ControlledUnifiedInput
              control={returnForm.control}
              name="packaging"
              label={t("returnModal.packagingLabel")}
              inputType="float"
              rightElement={<span>€</span>}
            />
            <ControlledUnifiedInput
              control={returnForm.control}
              name="packagingProtection"
              label={t("returnModal.packagingProtectionLabel")}
              inputType="float"
              rightElement={<span>€</span>}
            />
            <ControlledUnifiedInput
              control={returnForm.control}
              name="taxes"
              label={t("returnModal.taxesLabel")}
              inputType="float"
              rightElement={<span>€</span>}
            />
            <ControlledUnifiedInput
              control={returnForm.control}
              name="insurance"
              label={t("returnModal.insuranceLabel")}
              inputType="float"
              rightElement={<span>€</span>}
            />
            <ControlledUnifiedInput
              control={returnForm.control}
              name="tva"
              label={t("returnModal.tvaLabel")}
              inputType="float"
              rightElement={<span>€</span>}
            />
          </div>
        </fieldset>
      </form>
    </Modal>
  );
};
